import { Button as BaseButton } from 'rebass'
import styled, { withTheme, keyframes, css } from 'styled-components'
import { style, height, minHeight, opacity, position, boxShadow } from 'styled-system'
import { darken } from 'polished'

const animationName = keyframes`
from {
  opacity: 0;
  transform: scale3d(0.3, 0.3, 0.3);
}

50% {
  opacity: 1;
  transform: scale3d(1, 1, 1);
} 

100% {
  opacity: 1;
  transform: scale3d(1, 1, 1);
}`

const animation = ({ delay, theme }) => {
  return delay instanceof Array
    ? css`
        ${delay.map((d, index) =>
    index === 0
      ? css`
                animation: ${animationName} 0.5s forwards ${d} ease-out;
              `
      : css`
                @media screen and (min-width: ${theme.breakpoints[index - 1]}px) {
                  animation: ${animationName} 0.5s forwards ${d} ease-out;
                }
              `
  )}
      `
    : css`
        animation: ${animationName} 0.5s forwards ${delay} ease-out;
      `
}

const getSizeAttributes = ({ mini, size }) => {
  switch (size) {
    case 'sm':
      return {
        minHeight: mini ? '30px' : [2, 1]
      }
    case 'large':
      return {
        minHeight: mini ? '30px' : [4, 3]
      }
    default:
      return {
        minHeight: mini ? '30px' : [3, 2]
      }
  }
}

const hoverColor = style({
  prop: 'hoverColor',
  cssProperty: 'border-color',
  key: 'colors'
})

const Button = withTheme(props => {
  return (
    <BaseButton
      {...getSizeAttributes(props)}
      {...props}
      delay={props.delay}
      css={[
        `
          padding-top: 5px;
          padding-bottom: 5px;
          display: inline-flex;
          align-items: center;
          font-family: "Roboto";
          font-weight: 450;
          justify-content: center;
          transition: all .1s;
          ${
    props.active
      ? `
          background-color: ${darken(0.2, props.theme.colors.primary)};
          border-color: ${darken(0.2, props.theme.colors.primary)} !important;
        `
      : ''
    }
          ${
    props.noBgHover
      ? `
        :not([disabled]):hover{
          border-color: ${props.theme.colors.primary};
          box-shadow: ${props.theme.shadows[4]};
        }`
      : `
        :not([disabled]):hover{
          border-color: ${darken(0.2, props.theme.colors.primary)};
          box-shadow: ${props.theme.shadows[1]};
          background-color: ${darken(0.2, props.theme.colors.primary)};
          color: ${props.theme.colors.white.default};
        }`
    }
    ${props.noShadowHover ? ':not([disabled]):hover{ box-shadow: none; }' : ''}
          :active, :focus {
              outline: none;
          }
        `,
        height,
        minHeight,
        opacity,
        position,
        hoverColor,
        boxShadow,
        props =>
          props.buttonType === 'light'
            ? `
          background-color: transparent;
          border: 1px solid ${props.theme.colors.grey.light} !important;
          color: ${props.theme.colors.grey.primary};
          transition: all .5s;
      
          &:hover, &.hover {
            cursor: pointer;
            border-color: ${props.theme.colors.grey.primary};
            color: ${props.theme.colors.grey.primary};
            background-color: rgba(${props.theme.colors.grey.primary}, 0.1);
          }
      
          &.active {
            background-color: ${props.theme.colors.grey.primary};
            border-color: ${props.theme.colors.grey.primary};
            color: ${props.theme.colors.white.default};
          }`
            : '',
        props => (props.disabled ? `cursor:not-allowed;` : `cursor:pointer;`),
        props => (props.selected ? `background-color: ${props.theme.colors.red}` : ''),
        props.css
      ]}
    />
  )
})

Button.defaultProps = {
  bg: 'primary',
  borderRadius: 1,
  hoverColor: 'transparent',
  border: 'transparent'
}

const ButtonWithStyle = styled(Button)`
  ${props =>
    props.animated
      ? css`
          opacity: 0;
          ${animation}
        `
      : ''};
`

ButtonWithStyle.displayName = 'Button'

export default ButtonWithStyle
